import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { SectionSidebarNavButtonItem } from "@/components/SectionSidebarNav";

export interface PersonalSubNavRegistration {
  parentKey: string;
  items: SectionSidebarNavButtonItem[];
}

interface PersonalSubNavContextValue {
  registration: PersonalSubNavRegistration | null;
  setRegistration: (next: PersonalSubNavRegistration) => void;
  clearRegistration: (parentKey: string) => void;
}

const PersonalSubNavContext = createContext<PersonalSubNavContextValue | null>(null);

export function PersonalSubNavProvider({ children }: { children: ReactNode }) {
  const [registration, setRegistrationState] = useState<PersonalSubNavRegistration | null>(null);

  const setRegistration = useCallback((next: PersonalSubNavRegistration) => {
    setRegistrationState(next);
  }, []);

  const clearRegistration = useCallback((parentKey: string) => {
    setRegistrationState((current) => (current?.parentKey === parentKey ? null : current));
  }, []);

  const value = useMemo(
    () => ({ registration, setRegistration, clearRegistration }),
    [registration, setRegistration, clearRegistration],
  );

  return (
    <PersonalSubNavContext.Provider value={value}>{children}</PersonalSubNavContext.Provider>
  );
}

export function usePersonalSubNavRegistration(): PersonalSubNavRegistration | null {
  return useContext(PersonalSubNavContext)?.registration ?? null;
}

/**
 * Registriert Unterpunkte der 3. Ebene in der Personal-Sidebar.
 * Liefert `nestInParentNav`, solange die Seite im PersonalLayout mit Sidebar steckt.
 */
export function useRegisterPersonalSubNav(
  parentKey: string,
  items: SectionSidebarNavButtonItem[],
) {
  const ctx = useContext(PersonalSubNavContext);
  const setRegistration = ctx?.setRegistration;
  const clearRegistration = ctx?.clearRegistration;

  useEffect(() => {
    if (!setRegistration || !clearRegistration) return;
    setRegistration({ parentKey, items });
    return () => clearRegistration(parentKey);
  }, [setRegistration, clearRegistration, parentKey, items]);

  return { nestInParentNav: ctx != null };
}
